// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Huntress — the solo opponent. In a deep abstract the bot IS the product for one player, so this
// is a real engine, not a move-picker.
//
// THE PROBLEM THIS FILE SOLVES: the branching factor. A four-huntress 8×8 opening has roughly
// 80 (huntress, destination) pairs and ~20 arrows each — about 1,600 plies from one position, and
// 2.5 million after two. A plain "generate everything and sort" search reaches depth 1 and plays
// like a grabber. So the candidate list is built in TWO STAGES: score the destinations with the
// same evaluation the search uses (cheap, because the arrow has not been fired yet), keep the best
// Kq, then score that huntress's arrows and keep the best Ka. Width is a tier setting, so the
// difficulty ladder is a real ladder and not eval noise pretending to be one.
//
// THE EVALUATION is the standard two-metric territory measure, in its own words: flood the board
// from each side twice — once by QUEEN moves (how many turns to get a huntress onto that square)
// and once by KING steps (how many single paces) — and score every empty square for whoever gets
// there first. Queen distance is what actually decides who owns ground; king distance breaks the
// enormous number of queen-distance ties and is what stops the bot walking into a room it can enter
// but never fill. Mobility is added on top, weighted heavily early (when nothing is owned yet) and
// tapering as the board burns down, because a huntress with two moves left is dead whatever the
// map says.
//
// Two rules held over from the rest of the fleet: NO requestAnimationFrame anywhere (a backgrounded
// tab throttles it to nothing and a bot that cannot finish its turn is a hang, not a delay), and
// the sim drives this same search budgeted by DEPTH and a node cap rather than a wall clock, so
// tests/balance.test.ts is reproducible instead of machine-dependent.

import { makeRng, type Rng } from '@ben-gy/game-engine/rng';
import {
  EMPTY,
  applyPly,
  burntCells,
  cloneState,
  countPlies,
  genPlies,
  huntressesOf,
  isOver,
  kingDistance,
  packPly,
  queenDistance,
  rayCount,
  rayTargets,
  xOf,
  yOf,
  type State,
} from './rules';

export interface Tier {
  id: string;
  name: string;
  blurb: string;
  depth: number;
  timeMs: number;
  /** Destinations kept after the first pass. */
  kq: number;
  /** Arrows kept per destination. */
  ka: number;
  /** Novice: territory is ignored, so she chases mobility and never builds a wall. */
  flat: boolean;
  noise: number;
}

export const TIERS: Tier[] = [
  {
    id: 'tracker',
    name: 'Tracker',
    blurb: 'Keeps her options open and shoots where you were — but never plans a wall.',
    depth: 1,
    timeMs: 120,
    kq: 6,
    ka: 2,
    flat: true,
    noise: 26,
  },
  {
    id: 'ranger',
    name: 'Ranger',
    blurb: 'Reads the ground a few turns ahead and will fence you out of the open.',
    depth: 3,
    timeMs: 550,
    kq: 10,
    ka: 3,
    flat: false,
    noise: 4,
  },
  {
    id: 'warden',
    name: 'Warden',
    blurb: 'Searches deep, counts territory, and closes the door behind you.',
    // MEASURED, not guessed. The first cut of this tier was depth 4 with kq 14 / ka 5, and the width
    // made depth 4 unreachable: it burned its whole 1100ms budget, aborted, and returned a depth-3
    // answer — the SAME depth Ranger reaches in 70ms. Thirteen times the cost, a frozen main thread,
    // and no more strength. Narrowing the candidate lists is what buys the extra ply: 8x3 completes
    // a true depth 4 in ~0.5s here (~1.5s on a mid phone), inside the cap with room to spare, and
    // the cap is a safety net rather than the expected cost. Re-measure if you touch kq/ka.
    depth: 4,
    timeMs: 2000,
    kq: 8,
    ka: 3,
    flat: false,
    noise: 0,
  },
];

export const DEFAULT_TIER = 'ranger';

export function tierOf(id: string | null | undefined): Tier {
  if (typeof id === 'string') {
    const t = TIERS.find((x) => x.id === id);
    if (t) return t;
  }
  return TIERS[1];
}

const MATE = 1_000_000;

export interface SearchOpts {
  depth: number;
  /** Wall-clock cap in ms. 0 disables it — which is what the sim uses. */
  timeMs?: number;
  /** Hard node cap, so a sim run is bounded regardless of machine speed. */
  nodes?: number;
  kq?: number;
  ka?: number;
  flat?: boolean;
  noise?: number;
  rng?: Rng;
}

export interface SearchResult {
  ply: number;
  score: number;
  depth: number;
  nodes: number;
  ms: number;
}

// ── evaluation ──────────────────────────────────────────────────────────────────
const FAR = 99;

/** Territory weights, and how the mixture moves as the board burns down. */
const W_QUEEN = 34;
const W_KING = 12;
const W_MOBILITY_EARLY = 26;
const W_MOBILITY_LATE = 5;
const W_CENTRE = 2;

/**
 * Score the position for `me`. Positive is good for `me`.
 *
 * `phase` runs 0 (opening) → 1 (endgame) on the fraction of the board already burnt, and is what
 * slides the mixture from mobility toward territory. It is a pure function of the position, so
 * both peers and the sim agree on it without any extra state.
 */
export function evaluate(s: State, me: number, flat = false): number {
  const them = 1 - me;
  const openable = s.cells - s.k * 2;
  const phase = openable > 0 ? Math.min(1, burntCells(s) / openable) : 1;

  let mob = 0;
  for (const hu of huntressesOf(s, me)) mob += Math.sqrt(rayCount(s, s.pos[hu], -1));
  for (const hu of huntressesOf(s, them)) mob -= Math.sqrt(rayCount(s, s.pos[hu], -1));
  const wMob = W_MOBILITY_EARLY + (W_MOBILITY_LATE - W_MOBILITY_EARLY) * phase;

  if (flat) {
    // The beginner tier: mobility and nothing else. She stays loose and shoots reasonably, but she
    // cannot see ground she does not currently stand on, so she never builds a wall.
    return Math.round(mob * W_MOBILITY_EARLY);
  }

  const q0 = queenDistance(s, me);
  const q1 = queenDistance(s, them);
  const k0 = kingDistance(s, me);
  const k1 = kingDistance(s, them);

  const cx = (s.w - 1) / 2;
  const cy = (s.h - 1) / 2;
  let tQueen = 0;
  let tKing = 0;
  let centre = 0;

  for (let i = 0; i < s.cells; i++) {
    if (s.cell[i] !== EMPTY) continue;
    const a = q0[i];
    const b = q1[i];
    if (a === FAR && b === FAR) {
      // Nobody can ever use it. Worth nothing to either side.
    } else if (a < b) tQueen += 1;
    else if (b < a) tQueen -= 1;

    const ka = k0[i];
    const kb = k1[i];
    if (ka < kb) tKing += 1;
    else if (kb < ka) tKing -= 1;

    if (a < b) centre += 1 - (Math.abs(xOf(s, i) - cx) + Math.abs(yOf(s, i) - cy)) / (cx + cy || 1);
    else if (b < a) centre -= 1 - (Math.abs(xOf(s, i) - cx) + Math.abs(yOf(s, i) - cy)) / (cx + cy || 1);
  }

  // Territory only becomes the whole story once there is territory to own, so it ramps in.
  const wQueen = W_QUEEN * (0.45 + 0.55 * phase);
  return Math.round(tQueen * wQueen + tKing * W_KING + mob * wMob + centre * W_CENTRE);
}

// ── candidate generation ────────────────────────────────────────────────────────

interface Ctx {
  pool: State[];
  scratch: State;
  nodes: number;
  deadline: number;
  maxNodes: number;
  aborted: boolean;
  flat: boolean;
  kq: number;
  ka: number;
}

function copyInto(dst: State, src: State): void {
  dst.cell.set(src.cell);
  dst.pos.set(src.pos);
  dst.turn = src.turn;
  dst.ply = src.ply;
  dst.winner = src.winner;
  dst.reason = src.reason;
  dst.log.length = 0;
}

interface Cand {
  ply: number;
  score: number;
}

const BURNT_V = -2;

/** Total ray mobility of one side. The cheap currency both pruning stages are priced in. */
function sideMobility(s: State, side: number): number {
  let n = 0;
  for (const hu of huntressesOf(s, side)) {
    const at = s.pos[hu];
    if (at >= 0) n += rayCount(s, at, -1);
  }
  return n;
}

/**
 * The two-stage candidate list, and the reason the search reaches a useful depth at all.
 *
 * A four-huntress 8×8 position offers ~80 destinations and ~20 arrows from each. Scoring all 1,600
 * with the full territory evaluation would mean four board floods per candidate — measured at
 * roughly 280 evaluations per node, which is where the first version of this file spent its entire
 * budget and came back with depth 1.
 *
 * So the two pruning stages are priced in MOBILITY, which costs eight rays per huntress and no
 * floods at all: keep the destinations that leave this side the most room, then the arrows that
 * take the most room off the other side. The full territory evaluation is spent only where it
 * decides something — at the leaves. That is the standard split, and the width settings are what
 * the tiers actually move.
 */
export function candidatePlies(s: State, ctx: Ctx): number[] {
  const scratch = ctx.scratch;
  const me = s.turn;
  const them = 1 - me;
  const cx = (s.w - 1) / 2;
  const cy = (s.h - 1) / 2;
  const dests: number[] = [];
  const shots: number[] = [];
  const moves: Array<{ from: number; to: number; score: number }> = [];

  copyInto(scratch, s);
  for (const hu of huntressesOf(s, me)) {
    const from = s.pos[hu];
    if (from < 0) continue;
    dests.length = 0;
    rayTargets(s, from, -1, dests);
    for (const to of dests) {
      scratch.cell[from] = EMPTY;
      scratch.cell[to] = hu;
      scratch.pos[hu] = to;
      // Room to move from the new square, and a mild pull toward the middle where rays are longest.
      const room = rayCount(scratch, to, -1);
      const centre = 2 - (Math.abs(xOf(s, to) - cx) + Math.abs(yOf(s, to) - cy)) / Math.max(1, cx + cy);
      scratch.cell[to] = EMPTY;
      scratch.cell[from] = hu;
      scratch.pos[hu] = from;
      moves.push({ from, to, score: room * 4 + centre });
    }
  }
  if (!moves.length) return [];
  moves.sort((a, b) => b.score - a.score);
  const keepQ = moves.slice(0, Math.max(1, ctx.kq));

  const out: Cand[] = [];
  for (const mv of keepQ) {
    const hu = s.cell[mv.from];
    copyInto(scratch, s);
    scratch.cell[mv.from] = EMPTY;
    scratch.cell[mv.to] = hu;
    scratch.pos[hu] = mv.to;
    shots.length = 0;
    rayTargets(scratch, mv.to, -1, shots);
    const arrows: Cand[] = [];
    for (const arrow of shots) {
      scratch.cell[arrow] = BURNT_V;
      // What the shot costs them, minus what it costs us.
      const score = sideMobility(scratch, me) - sideMobility(scratch, them) * 2;
      scratch.cell[arrow] = EMPTY;
      arrows.push({ ply: packPly(mv.from, mv.to, arrow), score });
    }
    arrows.sort((a, b) => b.score - a.score);
    for (const a of arrows.slice(0, Math.max(1, ctx.ka))) out.push(a);
  }
  return out.map((c) => c.ply);
}

// ── the search ──────────────────────────────────────────────────────────────────

function negamax(ctx: Ctx, s: State, depth: number, alpha: number, beta: number, dist: number): number {
  if ((ctx.nodes & 31) === 0) {
    if (ctx.deadline > 0 && Date.now() > ctx.deadline) ctx.aborted = true;
    if (ctx.maxNodes > 0 && ctx.nodes > ctx.maxNodes) ctx.aborted = true;
  }
  if (ctx.aborted) return 0;
  ctx.nodes++;

  // A side with nothing to play has lost, right here. Prefer losing LATER and winning SOONER.
  if (countPlies(s) === 0) return -MATE + dist;
  if (depth <= 0) return evaluate(s, s.turn, ctx.flat);

  const plies = candidatePlies(s, ctx);
  if (!plies.length) return -MATE + dist;

  let best = -Infinity;
  const child = ctx.pool[dist];
  for (const ply of plies) {
    copyInto(child, s);
    applyPly(child, ply, undefined, false, false);
    const score = -negamax(ctx, child, depth - 1, -beta, -alpha, dist + 1);
    if (ctx.aborted) return 0;
    if (score > best) best = score;
    if (best > alpha) alpha = best;
    if (alpha >= beta) break;
  }
  return best;
}

function makeCtx(s: State, opts: SearchOpts, started: number, maxDepth: number): Ctx {
  return {
    pool: Array.from({ length: maxDepth + 3 }, () => cloneState(s)),
    scratch: cloneState(s),
    nodes: 0,
    deadline: opts.timeMs && opts.timeMs > 0 ? started + opts.timeMs : 0,
    maxNodes: opts.nodes ?? 0,
    aborted: false,
    flat: !!opts.flat,
    kq: opts.kq ?? 10,
    ka: opts.ka ?? 3,
  };
}

/** Iterative deepening from the root. Returns the best ply found within the budget. */
export function searchTurn(s: State, opts: SearchOpts): SearchResult {
  const started = Date.now();
  const maxDepth = Math.max(1, opts.depth);
  const ctx = makeCtx(s, opts, started, maxDepth);

  const root = candidatePlies(s, ctx);
  if (!root.length) return { ply: -1, score: -MATE, depth: 0, nodes: 0, ms: 0 };

  const noise = opts.noise ?? 0;
  const rng = opts.rng ?? makeRng((s.seed ^ (s.ply * 2654435761)) >>> 0);

  let bestPly = root[0];
  let bestScore = -Infinity;
  let reached = 0;

  for (let depth = 1; depth <= maxDepth; depth++) {
    let localBest = -Infinity;
    let localPly = bestPly;
    let alpha = -Infinity;
    const child = ctx.pool[0];

    // Search the previous iteration's best first — that is what makes deepening pay for itself.
    const ordered = [bestPly, ...root.filter((p) => p !== bestPly)];
    for (const ply of ordered) {
      copyInto(child, s);
      applyPly(child, ply, undefined, false, false);
      let score = -negamax(ctx, child, depth - 1, -Infinity, -alpha, 1);
      if (ctx.aborted) break;
      if (noise > 0) score += (rng() - 0.5) * 2 * noise;
      if (score > localBest) {
        localBest = score;
        localPly = ply;
      }
      if (score > alpha) alpha = score;
    }
    if (ctx.aborted) break;
    bestPly = localPly;
    bestScore = localBest;
    reached = depth;
    if (bestScore >= MATE - 8) break; // a forced win — deeper cannot improve on it
  }

  return { ply: bestPly, score: bestScore, depth: reached, nodes: ctx.nodes, ms: Date.now() - started };
}

export function optsForTier(t: Tier, seed = 0): SearchOpts {
  return {
    depth: t.depth,
    timeMs: t.timeMs,
    kq: t.kq,
    ka: t.ka,
    flat: t.flat,
    noise: t.noise,
    rng: makeRng((seed ^ 0x9e37) >>> 0),
  };
}

/**
 * A one-ply control for the balance sim: take the move that leaves you the most room and shoot the
 * square that costs the opponent the most, with no lookahead and no territory map. Deliberately NOT
 * the Tracker tier — a control the sim compares against must be a different animal, or "the bot
 * beats the control" is just the bot beating itself.
 */
export function greedyPly(s: State, rng: Rng): number {
  const me = s.turn;
  const dests: number[] = [];
  const shots: number[] = [];
  let best = -1;
  let bestScore = -Infinity;
  const probe = cloneState(s);
  for (const hu of huntressesOf(s, me)) {
    const from = s.pos[hu];
    if (from < 0) continue;
    dests.length = 0;
    rayTargets(s, from, -1, dests);
    for (const to of dests) {
      copyInto(probe, s);
      probe.cell[from] = EMPTY;
      probe.cell[to] = hu;
      probe.pos[hu] = to;
      shots.length = 0;
      rayTargets(probe, to, -1, shots);
      for (const arrow of shots) {
        probe.cell[arrow] = -2;
        let mine = 0;
        for (const h2 of huntressesOf(probe, me)) mine += rayCount(probe, probe.pos[h2], -1);
        let theirs = 0;
        for (const h2 of huntressesOf(probe, 1 - me)) theirs += rayCount(probe, probe.pos[h2], -1);
        probe.cell[arrow] = EMPTY;
        const score = mine - theirs + rng() * 2;
        if (score > bestScore) {
          bestScore = score;
          best = packPly(from, to, arrow);
        }
      }
    }
  }
  return best;
}

export function randomPly(s: State, rng: Rng): number {
  const plies = genPlies(s);
  if (!plies.length) return -1;
  return plies[Math.floor(rng() * plies.length)];
}

/** The one entry point the game screen uses. */
export function chooseAny(s: State, opts: SearchOpts): number {
  if (isOver(s)) return -1;
  return searchTurn(s, opts).ply;
}
